/* Importaciones de React */
import React, { useState, useEffect, useContext } from 'react'

/* Importaciones de @emotion/react */
import { useTheme } from '@emotion/react'

/* Importaciones de MUI */
import { Stack, TextField, InputAdornment, Typography } from '@mui/material'
import { Search, AirOutlined, PendingActionsOutlined, Replay, SearchOff } from '@mui/icons-material'

/* Importaciones de React-Icons */
import { LuServerOff } from 'react-icons/lu'

/* Importaciones del modulo de Shared */
import { Message, SectionCard, SkeletonLoad, Table } from '../../Shared'

/* Importaciones de elementos compartidos del modulo de mi carrito */
import { PendingPay } from './ShoppingCartSharedComponents'

/* Importación del context del modulo de ShoppingCart */
import ShoppingCartContext from './ShoppingCartContext'

export default function PendingPaysList({ addPendingPayHandler }){
    /* Variables */
    const { carts, actualCart } = useContext(ShoppingCartContext)
    const cart = carts[actualCart]

    /* Estados */
    const [ searchWords, setSearchWords ] = useState('')
    const [ loadingPendingPays, setLoadingPendingPays ] = useState(true)
    const [ pendingPays, setPendingPays ] = useState(null)
    const [ pendingPaysFoundAt, setPendingPaysFoundAt ] = useState(null)
    const [ pendingPaysMessageTitle, setPendingPaysMessageTitle ] = useState('')
    const [ pendingPaysMessageContent, setPendingPaysMessageContent ] = useState('')

    /* Funciones */
    

    /* Funciones asincronas */
    const getPendingPays = async () => {
        setLoadingPendingPays(true);
        try {
            let url = 'tickets/pending';
            if (searchWords !== '') {
                url += `/${searchWords}`;
            }
            const response = await fetch(`${sessionStorage.getItem('localhost')}/api/v2/${url}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'Cache-Control': 'no-cache',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setPendingPays(data?.pendingPays);
                setPendingPaysFoundAt(data?.pendingPays);

                const sessions = JSON.parse(localStorage.getItem('sessions'));
                sessions[(JSON.parse(sessionStorage.getItem('user'))).id]['pendingPays'] = data?.pendingPays;
                localStorage.setItem('sessions', JSON.stringify(sessions));

                if (data?.pendingPays?.length === 0) {
                    setPendingPaysMessageTitle('Sin pagos pendientes');
                    setPendingPaysMessageContent('Para poder visualizar los pagos pendientes, realice un pago pendiente o agregue artículos en un carrito.');
                }
            } else {
                throw new Error('Hubo un problema con el servidor-Ocurrió un problema al momento de traer los pagos pendientes, inténtelo más tarde.');
            }
        } catch (error) {
            const requestErrorMessage = error.message.split('-');
            setPendingPaysMessageTitle(requestErrorMessage[0]);
            setPendingPaysMessageContent(requestErrorMessage[1]);
            setPendingPays(null);
        } finally {
            setLoadingPendingPays(false);
        }
    };
    const debounceTimeout = 1000;
    let debounceTimer;

    const handleSearch = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            getPendingPays();
        }, debounceTimeout);
    };

    /* useEffects */
    useEffect(() => handleSearch(), [])
    useEffect(() => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (searchWords === '') {
                getPendingPays();
            } else {
                const currentSearchWords = searchWords;
                getPendingPays().then(() => {
                    if (currentSearchWords !== searchWords) {
                        return; // Discard execution if searchWords changed
                    }
                });
            }
        }, debounceTimeout);

        return () => clearTimeout(debounceTimer); // Cleanup on unmount or searchWords change
    }, [searchWords]);

    /* Componente */
    return (
        <Stack
            padding='5px 0px 0px 0px'
            direction='column'
            spacing={0.5}
        >   
            <TextField 
                variant='outlined'
                label='UUID, Folio, Nombre de cliente del ticket'
                placeholder='Introduce el UUID, Folio, Nombre de cliente para localizar su pago pendiente.'
                value={searchWords}
                disabled={loadingPendingPays}
                onChange={e => setSearchWords(e.target.value.replace(/[^a-zA-Z0-9- ]/g, ''))}
                focused
                InputProps={{
                    startAdornment: <InputAdornment position='start'><Search /></InputAdornment>
                }}
                fullWidth
                />
            {loadingPendingPays && (
                <Stack
                    width={552}
                    height={292.5}
                    direction='column'
                    spacing={0.5}
                >
                    <SkeletonLoad 
                        types={['rectangular']}
                        numberOfItems={1}
                        randomHeight
                        heightRange={[53.1333, 53.1333]}
                    />

                    <Stack
                        direction='column'
                        padding='3px'
                        width={552}
                        height={230.5}
                    >
                        <SkeletonLoad 
                            types={['rectangular']}
                            numberOfItems={3}
                            randomHeight
                            heightRange={[61.5, 61.5]}
                        />
                    </Stack>
                </Stack>
            )}
            

            {!loadingPendingPays && (pendingPays === null || pendingPays?.length === 0) && (
                <Stack
                    width={600}
                    height={365.133}
                    direction='column'
                    justifyContent='center'
                >
                    <Message
                        title={pendingPaysMessageTitle}
                        message={pendingPaysMessageContent}
                        icon={pendingPays === null ? <LuServerOff fontSize={50} /> : <AirOutlined sx={{ fontSize: 50 }} />}
                        callback={pendingPays === null ? () => {} : () => {}}
                        buttonMessage={
                            pendingPays === null ? 
                                <Stack
                                    direction='row'
                                    alignItems='center'
                                    justifyContent='center'
                                    gap={2}
                                    color='#ffffff'
                                    fontWeight='600'
                                    border='2px solid #ff4500'
                                    borderRadius={3}
                                    bgcolor='#ff4500'
                                    paddingY={0.3}
                                    paddingX={1}
                                    sx={{
                                        '&:hover': {
                                            color: '#dddddd',
                                            border: '2px solid #d53a00',
                                            bgcolor: '#d53a00'
                                        }
                                    }}
                                >
                                    <Replay />

                                    <Typography
                                        fontSize={15}
                                        onClick={() => {
                                            getPendingPays()
                                        }
                                        }
                                    >
                                        Volver intentar
                                    </Typography>
                                </Stack>
                            : 
                            <Typography
                                fontSize={18}
                                color='textSecondary'
                            >
                                Sin resultados
                            </Typography>
                        }
                    />
                </Stack>
            )}

            {!loadingPendingPays && pendingPays?.length > 0 && <>

                {pendingPaysFoundAt?.length > 0 && (
                    <Stack
                        direction='column'
                        spacing={1}
                        padding='3px'
                        sx={{
                            height: 230.5,
                            overflowX: 'hidden',
                            overflowY: 'auto'
                        }}
                    >
                        {pendingPaysFoundAt.map(pendingPay => ( 
                            <PendingPay 
                                pendingPay={pendingPay}
                                addPendingPayHandler={addPendingPayHandler} 
                            /> 
                        ))}
                    </Stack>
                )}

                {pendingPaysFoundAt?.length === 0 && (
                    <Stack
                        width={552}
                        height={230.5}
                        direction='column'
                        justifyContent='center'
                    >
                        <Message 
                            title='Sin resultados'
                            message='Revise los datos de su ticket(uuid ó compania) para localizar los tickets que se ocupan.'
                            icon={<SearchOff sx={{ fontSize: 50 }} />}
                        />
                    </Stack>
                )}
            </>}
        </Stack>
    )
}